import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useEmailNotifications } from '../hooks/useEmailNotifications';
import { FileEdit, FileCheck, FileSpreadsheet, FileText, Wallet, Lock, Ban, CheckCircle2, FileDown } from 'lucide-react';
import CompromiseModal from '../components/dashboard/CompromiseModal';
import RegulModal from '../components/dashboard/RegulModal';
import ActeModal from '../components/dashboard/ActeModal';
import EncaissementModal from '../components/dashboard/EncaissementModal';
import EditTransactionModal from '../components/validation/EditTransactionModal';
import ViewTransactionModal from '../components/validation/ViewTransactionModal';
import ViewRegulModal from '../components/validation/ViewRegulModal';
import ClotureModal from '../components/dashboard/ClotureModal';
import ValidationFilters from '../components/validation/filters/ValidationFilters';
import ExportModal from '../components/validation/ExportModal';
import TransactionTable from '../components/validation/TransactionTable';
import { useValidationData } from '../components/validation/hooks/useValidationData';
import { useValidationFilters } from '../components/validation/hooks/useValidationFilters';
import { useAuth } from '../contexts/AuthContext';
import { Transaction } from '../types/dashboard';
import CancelTransactionModal from '../components/dashboard/CancelTransactionModal';

export default function ProductionValidation() {
  const { sendStatusChangeNotification } = useEmailNotifications();
  const { user } = useAuth();
  const {
    transactions,
    filteredTransactions,
    setFilteredTransactions,
    loading,
    error,
    agencies,
    negotiators,
    loadData
  } = useValidationData();

  const {
    searchTerm,
    selectedStatus,
    selectedCompany,
    selectedAgency,
    selectedNegotiator,
    showRegulOnly,
    showClosedAndCancelled,
    encaissementDateRange,
    appliedEncaissementDateRange,
    compromiseDateRange,
    appliedCompromiseDateRange,
    applyEncaissementDateFilter,
    clearEncaissementDateFilter,
    applyCompromiseDateFilter,
    clearCompromiseDateFilter,
    setSearchTerm,
    setSelectedStatus,
    setSelectedCompany,
    setSelectedAgency,
    setSelectedNegotiator,
    setShowRegulOnly,
    setShowClosedAndCancelled,
    setEncaissementDateRange,
    setCompromiseDateRange
  } = useValidationFilters(transactions, agencies, setFilteredTransactions);

  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isCompromiseModalOpen, setIsCompromiseModalOpen] = useState(false);
  const [isRegulModalOpen, setIsRegulModalOpen] = useState(false);
  const [isViewRegulModalOpen, setIsViewRegulModalOpen] = useState(false);
  const [isActeModalOpen, setIsActeModalOpen] = useState(false);
  const [isEncaissementModalOpen, setIsEncaissementModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isClotureModalOpen, setIsClotureModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const displayedTransactions = showClosedAndCancelled 
    ? filteredTransactions 
    : filteredTransactions.filter(transaction => 
        !['cloture', 'annule'].includes(transaction.status)
      );

  const calculateNetFees = (transaction: Transaction) => {
    const totalRetrocessions = transaction.retrocessions?.reduce((sum, r) => sum + r.amount, 0) || 0;
    return transaction.fees_without_tax - totalRetrocessions;
  };

  const handleCancel = (transaction: Transaction) => {
    console.log("Opening cancel modal for transaction:", transaction);
    setSelectedTransaction(transaction);
    setIsCancelModalOpen(true);
  };

  const handleCancelTransaction = async (cancellationDate: string, cancellationReason: string) => {
    if (!selectedTransaction || !user) return;

    try {
      const { error: updateError } = await supabase
        .from('transactions')
        .update({ 
          status: 'annule',
          cancelled_at: new Date().toISOString(),
          cancellation_date: cancellationDate,
          cancellation_reason: cancellationReason
        })
        .eq('id', selectedTransaction.id);

      if (updateError) throw updateError;

      await supabase
        .from('transaction_logs')
        .insert([{
          transaction_id: selectedTransaction.id,
          user_id: user.id,
          event_type: 'cancellation',
          event_data: {
            previous_status: selectedTransaction.status,
            cancelled_at: new Date().toISOString(),
            cancellation_date: cancellationDate,
            cancellation_reason: cancellationReason
          }
        }]);

      loadData();
      closeAllModals();
    } catch (err: any) {
      console.error('Error canceling transaction:', err);
    }
  };

  const closeAllModals = () => {
    console.log('Closing all modals');
    setSelectedTransaction(null);
    setIsCompromiseModalOpen(false);
    setIsRegulModalOpen(false);
    setIsViewRegulModalOpen(false);
    setIsActeModalOpen(false);
    setIsEncaissementModalOpen(false);
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setIsClotureModalOpen(false);
    setIsExportModalOpen(false);
    setIsCancelModalOpen(false);
  };

  const handleViewTransaction = (transaction: Transaction) => {
    console.log('Opening ViewTransactionModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsViewModalOpen(true);
  };

  const handleEditTransaction = (transaction: Transaction) => {
    console.log('Opening EditTransactionModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsEditModalOpen(true);
  };

  const handleCompromiseTransaction = (transaction: Transaction) => {
    console.log('Opening CompromiseModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsCompromiseModalOpen(true);
  };

  const handleRegulTransaction = (transaction: Transaction) => {
    console.log('Opening RegulModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsRegulModalOpen(true);
  };

  const handleViewRegulTransaction = (transaction: Transaction) => {
    console.log('Opening ViewRegulModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsViewRegulModalOpen(true);
  };

  const handleActeTransaction = (transaction: Transaction) => {
    console.log('Opening ActeModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsActeModalOpen(true);
  };

  const handleEncaissementTransaction = (transaction: Transaction) => {
    console.log('Opening EncaissementModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsEncaissementModalOpen(true);
  };

  const handleClotureTransaction = (transaction: Transaction) => {
    console.log('Opening ClotureModal for transaction:', transaction);
    setSelectedTransaction(transaction);
    setIsClotureModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* ── Page Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-7 py-6 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <FileDown size={22} className="text-white/80" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Validation Production</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-indigo-300 font-semibold">{displayedTransactions.length}</span>
                {' '}transaction{displayedTransactions.length !== 1 ? 's' : ''} affichée{displayedTransactions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          <button
            onClick={() => setIsExportModalOpen(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-slate-900 bg-white hover:bg-indigo-50 shadow-md transition-all shrink-0"
          >
            <FileDown size={16} className="text-indigo-600" />
            Exporter
          </button>
        </div>
      </div>

      <ValidationFilters
        searchTerm={searchTerm}
        selectedStatus={selectedStatus}
        selectedCompany={selectedCompany}
        selectedAgency={selectedAgency}
        selectedNegotiator={selectedNegotiator}
        showRegulOnly={showRegulOnly}
        showClosedAndCancelled={showClosedAndCancelled}
        encaissementDateRange={encaissementDateRange}
        appliedEncaissementDateRange={appliedEncaissementDateRange}
        compromiseDateRange={compromiseDateRange}
        appliedCompromiseDateRange={appliedCompromiseDateRange}
        applyEncaissementDateFilter={applyEncaissementDateFilter}
        clearEncaissementDateFilter={clearEncaissementDateFilter}
        applyCompromiseDateFilter={applyCompromiseDateFilter}
        clearCompromiseDateFilter={clearCompromiseDateFilter}
        agencies={agencies}
        negotiators={negotiators}
        onSearchChange={setSearchTerm}
        onStatusChange={setSelectedStatus}
        onCompanyChange={setSelectedCompany}
        onAgencyChange={setSelectedAgency}
        onNegotiatorChange={setSelectedNegotiator}
        onRegulOnlyChange={setShowRegulOnly}
        onShowClosedAndCancelledChange={setShowClosedAndCancelled}
        onEncaissementDateRangeChange={setEncaissementDateRange}
        onCompromiseDateRangeChange={setCompromiseDateRange}
      />

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          <span>{error}</span>
        </div>
      )}

      <TransactionTable
        loading={loading}
        displayedTransactions={displayedTransactions}
        error={error}
        calculateNetFees={calculateNetFees}
        onViewTransaction={handleViewTransaction}
        onEditTransaction={handleEditTransaction}
        onCancelTransaction={handleCancel}
        onCompromiseTransaction={handleCompromiseTransaction}
        onRegulTransaction={handleRegulTransaction}
        onActeTransaction={handleActeTransaction}
        onEncaissementTransaction={handleEncaissementTransaction}
        onClotureTransaction={handleClotureTransaction}
        onViewRegulTransaction={handleViewRegulTransaction}
      />

      {/* Modales */}
      <CompromiseModal
        isOpen={isCompromiseModalOpen}
        onClose={() => {
          setIsCompromiseModalOpen(false);
          setSelectedTransaction(null);
        }}
        onValidate={async (compromiseDate, expectedActeDate) => {
          if (!selectedTransaction || !user) return;

          try {
            const { error: updateError } = await supabase
              .from('transactions')
              .update({
                status: 'compromis',
                compromise_date: compromiseDate,
                expected_acte_date: expectedActeDate
              })
              .eq('id', selectedTransaction.id);

            if (updateError) throw updateError;

            await supabase
              .from('transaction_logs')
              .insert([{
                transaction_id: selectedTransaction.id,
                user_id: user.id,
                event_type: 'compromise',
                event_data: {
                  compromise_date: compromiseDate,
                  expected_acte_date: expectedActeDate
                }
              }]);

            loadData();
            setIsCompromiseModalOpen(false);
            setSelectedTransaction(null);
          } catch (err: any) {
            console.error('Error updating transaction:', err);
          }
        }}
      />

      <RegulModal
        transaction={selectedTransaction}
        isOpen={isRegulModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
        onSubmit={async (comment) => {
          if (!selectedTransaction || !user) return;

          try {
            const { error: updateError } = await supabase
              .from('transactions')
              .update({ 
                regul: true,
                commentaire_regul: comment
              })
              .eq('id', selectedTransaction.id);

            if (updateError) throw updateError;

            loadData();
            closeAllModals();
          } catch (err: any) {
            console.error('Error requesting regul:', err);
          }
        }}
      />

      <ViewRegulModal
        transaction={selectedTransaction}
        isOpen={isViewRegulModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
      />

      <ActeModal
        isOpen={isActeModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
        onValidate={async (acteDate, invoiceDate, invoiceNumber) => {
          if (!selectedTransaction || !user) return;

          try {
            const { error: updateError } = await supabase
              .from('transactions')
              .update({
                status: 'acte',
                acte_date: acteDate,
                invoice_date: invoiceDate,
                invoice_number: invoiceNumber
              })
              .eq('id', selectedTransaction.id);

            if (updateError) throw updateError;

            await supabase
              .from('transaction_logs')
              .insert([{
                transaction_id: selectedTransaction.id,
                user_id: user.id,
                event_type: 'acte',
                event_data: {
                  acte_date: acteDate,
                  invoice_date: invoiceDate,
                  invoice_number: invoiceNumber
                }
              }]);

            loadData();
            closeAllModals();
          } catch (err: any) {
            console.error('Error updating transaction:', err);
          }
        }}
      />

      <EncaissementModal
        isOpen={isEncaissementModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
        onValidate={async (encaissementDate) => {
          if (!selectedTransaction || !user) return;

          try {
            const { error: updateError } = await supabase
              .from('transactions')
              .update({
                status: 'encaisse',
                encaissement_date: encaissementDate
              })
              .eq('id', selectedTransaction.id);

            if (updateError) throw updateError;

            // Envoyer une notification email pour le changement de statut
            try {
              await sendStatusChangeNotification(selectedTransaction.id, selectedTransaction.status, 'encaisse');
            } catch (emailError) {
              console.error('Error sending status change notification:', emailError);
              // Ne pas faire échouer la mise à jour si l'email échoue
            }

            await supabase
              .from('transaction_logs')
              .insert([{
                transaction_id: selectedTransaction.id,
                user_id: user.id,
                event_type: 'encaissement',
                event_data: {
                  encaissement_date: encaissementDate
                }
              }]);

            loadData();
            closeAllModals();
          } catch (err: any) {
            console.error('Error updating transaction:', err);
          }
        }}
      />

      <EditTransactionModal
        transaction={selectedTransaction}
        isOpen={isEditModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
        onSuccess={() => {
          loadData();
          closeAllModals();
        }}
      />

      <ViewTransactionModal
        transaction={selectedTransaction}
        isOpen={isViewModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
      />

      <ClotureModal
        isOpen={isClotureModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
        onValidate={() => {
          if (!selectedTransaction) return;
          loadData();
          closeAllModals();
        }}
        transactionId={selectedTransaction?.id || ''}
        retrocessions={selectedTransaction?.retrocessions || []}
      />

      <CancelTransactionModal
        isOpen={isCancelModalOpen}
        onClose={() => {
          closeAllModals();
          loadData();
        }}
        onValidate={(cancellationDate, cancellationReason) => {
          handleCancelTransaction(cancellationDate, cancellationReason);
          setIsCancelModalOpen(false);
          setSelectedTransaction(null);
        }}
        transactionId={selectedTransaction?.id || ''}
      />

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => {
          closeAllModals();
        }}
        transactions={filteredTransactions}
      />
    </div>
  );
}